import { useGetApiAdminAuditLog } from '@sit/client-shared';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/__next__';
import {
  FlatTable,
  FlatTableBody,
  FlatTableCell,
  FlatTableHead,
  FlatTableHeader,
  FlatTableRow,
} from 'carbon-react/lib/components/flat-table';
import Loader from 'carbon-react/lib/components/loader/__next__';
import { Option, Select } from 'carbon-react/lib/components/select';
import { useState } from 'react';
import { useCompanyTreeSettingsIdInPathParams } from '../helpers';

const PAGE_SIZE_OPTIONS = ['25', '50', '100'] as const;
type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];

export function CompanyAuditLogPage() {
  const companyTreeSettingsId = useCompanyTreeSettingsIdInPathParams();
  const [limit, setLimit] = useState<PageSizeOption>('50');
  const [after, setAfter] = useState<string | undefined>(undefined);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const { data, isLoading } = useGetApiAdminAuditLog({
    queryParams: {
      companyTreeSettingsId,
      limit,
      after,
    },
  });

  const handleNext = () => {
    const endCursor = data?.pageInfo.endCursor;
    if (!endCursor) return;
    setCursorStack((prev) => [...prev, after ?? '']);
    setAfter(endCursor);
  };

  const handlePrevious = () => {
    const prevCursor = cursorStack[cursorStack.length - 1];
    setCursorStack((stack) => stack.slice(0, -1));
    setAfter(prevCursor || undefined);
  };

  const handleLimitChange = (value: string) => {
    setLimit(value as PageSizeOption);
    setAfter(undefined);
    setCursorStack([]);
  };

  if (isLoading || !data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <Loader showLabel={false} loaderType="ring" />
      </Box>
    );
  }

  const entries = data.edges.map((edge) => edge.node);

  return (
    <Box mt={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <span>{data.totalCount} total entries</span>
        <Box width="100px">
          <Select
            label="Per page"
            value={limit}
            onChange={(e) => handleLimitChange(e.target.value)}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <Option key={size} value={size} text={`${size}`} />
            ))}
          </Select>
        </Box>
      </Box>
      <FlatTable colorTheme="transparent-white" overflowX="auto" width="100%">
        <FlatTableHead>
          <FlatTableRow>
            <FlatTableHeader>Timestamp</FlatTableHeader>
            <FlatTableHeader>Actor</FlatTableHeader>
            <FlatTableHeader>Object Type</FlatTableHeader>
            <FlatTableHeader>Object Key</FlatTableHeader>
            <FlatTableHeader>Action</FlatTableHeader>
            <FlatTableHeader>Field</FlatTableHeader>
            <FlatTableHeader>Before</FlatTableHeader>
            <FlatTableHeader>After</FlatTableHeader>
            <FlatTableHeader>Source</FlatTableHeader>
            <FlatTableHeader>Mode</FlatTableHeader>
          </FlatTableRow>
        </FlatTableHead>
        <FlatTableBody>
          {entries.length === 0 ? (
            <FlatTableRow>
              <FlatTableCell colspan={10}>No audit log entries.</FlatTableCell>
            </FlatTableRow>
          ) : (
            entries.map((entry) => (
              <FlatTableRow key={entry.id}>
                <FlatTableCell>
                  {new Date(entry.timestamp).toLocaleString()}
                </FlatTableCell>
                <FlatTableCell>{entry.user}</FlatTableCell>
                <FlatTableCell>{entry.objectType}</FlatTableCell>
                <FlatTableCell>{entry.objectKey}</FlatTableCell>
                <FlatTableCell>{entry.action ?? '—'}</FlatTableCell>
                <FlatTableCell>{entry.fieldName}</FlatTableCell>
                <FlatTableCell>{entry.fieldBefore ?? '—'}</FlatTableCell>
                <FlatTableCell>{entry.fieldAfter ?? '—'}</FlatTableCell>
                <FlatTableCell>{entry.source}</FlatTableCell>
                <FlatTableCell>{entry.accessMode}</FlatTableCell>
              </FlatTableRow>
            ))
          )}
        </FlatTableBody>
      </FlatTable>
      <Box display="flex" justifyContent="flex-end" gap={2} my={2}>
        <Button
          variantType="secondary"
          disabled={cursorStack.length === 0}
          onClick={handlePrevious}
        >
          Previous
        </Button>
        <Button
          variantType="secondary"
          disabled={!data.pageInfo.hasNextPage}
          onClick={handleNext}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}
